"use client";

import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { CopyButton } from "./CopyButton";
import { highlight, type CodeLanguage } from "./highlight";
import { MONO } from "./styles";

const LANGUAGE_LABEL: Record<CodeLanguage, string> = {
  json: "JSON",
  shell: "Shell",
  javascript: "JavaScript",
  python: "Python",
  http: "HTTP",
  text: "Text",
};

/**
 * A code sample's body. Long lines scroll inside the box (never the page);
 * past `collapseAfter` lines the box is capped in height with a button to
 * show everything.
 */
export function CodePre({
  code,
  language,
  collapseAfter,
}: {
  code: string;
  language: CodeLanguage;
  collapseAfter?: number;
}) {
  const lines = useMemo(() => code.split("\n").length, [code]);
  const content = useMemo(() => highlight(code, language), [code, language]);
  const collapsible = collapseAfter !== undefined && lines > collapseAfter;
  const [expanded, setExpanded] = useState(false);
  const capped = collapsible && !expanded;

  return (
    <>
      <pre
        // Focusable so a keyboard can scroll it (the box scrolls on its own).
        tabIndex={0}
        className={`${MONO} overflow-x-auto px-4 py-3 text-[12.5px] leading-[1.65] text-[#c7d5e0] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#66c0f4] ${
          capped ? "max-h-[22rem] overflow-y-auto" : ""
        }`}
      >
        <code>{content}</code>
      </pre>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1.5 border-t border-[#2a475e]/60 py-2 text-xs font-medium text-[#66c0f4] transition-colors hover:bg-[#16202d]"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden />
          {expanded ? "Collapse" : `Show all ${lines} lines`}
        </button>
      )}
    </>
  );
}

export function CodeBlock({
  code,
  language = "text",
  title,
  collapseAfter,
  className = "",
}: {
  code: string;
  language?: CodeLanguage;
  /** Shown in the bar above the code; defaults to the language name. */
  title?: string;
  collapseAfter?: number;
  className?: string;
}) {
  return (
    <div className={`min-w-0 overflow-hidden rounded-lg border border-[#2a475e]/70 bg-[#0e1620] ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-[#2a475e]/60 bg-[#111a25] py-1 pl-3 pr-1.5">
        <span className={`${MONO} truncate text-[11px] uppercase tracking-wider text-[#8f98a0]`}>
          {title ?? LANGUAGE_LABEL[language]}
        </span>
        <CopyButton text={code} />
      </div>
      <CodePre code={code} language={language} collapseAfter={collapseAfter} />
    </div>
  );
}
